<template>
  <div class="mt-4 mx-3 row white-box px-3 py-4">
    <div class="white-box-top"></div>
    <div class="col-md-4 col-lg-2">
      <nav class="left-nav">
        <div class="items">
          <button
            v-for="event in events"
            :key="event.id"
            class="btn w-100 text-start"
            v-on:click="activeEvent = event"
            :class="event.id === activeEvent?.id ? 'btn-primary' : 'btn-simple'"
          >
            {{ event.name }}
          </button>
        </div>
      </nav>
    </div>
    <div
      v-if="activeEvent"
      class="
        col-md-8 col-lg-10
        d-flex
        align-items-center
        flex-column
        custom-input
      "
    >
      <div class="row align-self-start">
        <div class="col-auto p-0 m-0 mb-3" v-for="l in languages" :key="l.code">
          <button
            class="btn btn-tab"
            :class="{ active: l.code === selectedLanguage }"
            v-on:click="selectedLanguage = l.code"
          >
            <span class="semibold-14">{{ l.name }}</span>
          </button>
        </div>
      </div>
      <div class="event">
        <b>{{ activeEvent.name }}</b>
      </div>
      <div
        v-for="action in activeEvent.eventActions.filter(
          (e) => e.language_code === selectedLanguage
        )"
        :key="action.id"
        class="d-flex flex-column align-items-center"
      >
        <div class="event-line"></div>
        <div class="action" style="min-width: 750px">
          <div class="row custom-input whitebg-input mb-3">
            <div class="col-4">
              <select v-model="action.app_id" class="form-control">
                <option v-for="app in apps" :key="app.id" :value="app.app_id">
                  {{ app.name }}
                </option>
              </select>
            </div>
            <div class="col">
              <input
                class="form-control"
                type="text"
                v-model="action.title"
                :placeholder="$t(`events.subject`)"
              />
            </div>
          </div>
          <div v-if="getAppFormat(action.app_id) === 'mjml'">
            <div v-show="!showMjmlCode[action.id]">
              <div
                :id="'mjml-editor-' + action.id"
                class="mjml-editor-container"
              ></div>
            </div>
            <div v-if="showMjmlCode[action.id]" class="mt-2 w-100">
              <textarea
                class="form-control"
                style="height: 300px; font-family: monospace"
                :value="getMjmlCode(action)"
                @input="updateMjmlFromCode(action, $event.target.value)"
              ></textarea>
            </div>
            <div class="mt-2 text-end">
              <button
                class="btn btn-simple btn-sm"
                @click="toggleMjmlCode(action)"
              >
                {{
                  showMjmlCode[action.id] ? "Hide MJML Code" : "Show MJML Code"
                }}
              </button>
            </div>
          </div>
          <MarkdownEditor
            v-else
            v-model="action.text"
            :variables="activeEvent.EventVariables"
          />
          <div class="action-toolbar mt-3">
            <div class="action-toolbar-left">
              <div v-if="action.id" class="test-email">
                <button
                  class="btn btn-outline-primary btn-sm"
                  @click="openTest(action)"
                >
                  <span class="me-1">&#9654;</span>
                  Test event
                </button>
                <div
                  v-if="testActionId === action.id"
                  class="test-email-form mt-2"
                >
                  <div class="small text-muted mb-3">
                    The selected order provides the template data. The test is
                    sent only to the address below.
                  </div>
                  <div class="row g-3">
                    <div class="col-md-6">
                      <label class="form-label">Recipient email</label>
                      <input
                        v-model="testForm.email"
                        class="form-control"
                        type="email"
                        placeholder="name@example.com"
                        required
                      />
                    </div>
                    <div class="col-md-6">
                      <label class="form-label"
                        >Phone <span class="text-muted">(optional)</span></label
                      >
                      <input
                        v-model="testForm.phone"
                        class="form-control"
                        type="text"
                        placeholder="Phone number"
                      />
                    </div>
                    <div class="col-md-8">
                      <label class="form-label">Order data</label>
                      <v-select
                        v-model="testForm.order_id"
                        :options="testOrders"
                        label="label"
                        :reduce="(order) => order.id"
                        placeholder="Select a successful order"
                      />
                    </div>
                    <div class="col-md-4 d-flex align-items-end">
                      <button
                        class="btn btn-primary w-100"
                        :disabled="
                          testSending || !testForm.email || !testForm.order_id
                        "
                        @click="sendTest(action)"
                      >
                        {{ testSending ? "Sending..." : "Send test event" }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div class="variables-section">
                <button
                  class="btn btn-link p-0 text-decoration-none"
                  @click="toggleVariables(action.id)"
                >
                  {{ showVariables[action.id] ? "Hide" : "Show" }}
                  {{ $t("events.variables") }}
                </button>
                <div v-if="showVariables[action.id]" class="mt-2">
                  <span
                    v-for="variable in activeEvent.EventVariables"
                    :key="variable.id"
                    class="mt-1 mx-1 d-inline-block"
                  >
                    <span class="badge badge-outline">{{
                      variable.variable
                    }}</span>
                    {{ variable.description }}
                  </span>
                </div>
              </div>
            </div>
            <button v-on:click="convert(action)" class="btn btn-primary">
              {{ $t("finance.save") }}
            </button>
          </div>
        </div>
      </div>
      <div class="event-line"></div>
      <button class="btn btn-type-2" v-on:click="addNew">
        <span>{{ $t("companies.addNew") }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import http from "../../modules/http";
import { cleanTables } from "../../modules/markdownTable";
import MarkdownEditor from "../inputs/MarkdownEditor";
import { useStore } from "vuex";
import "grapesjs/dist/css/grapes.min.css";
import grapesjs from "grapesjs/dist/grapes.min";
import grapesJSMJML from "grapesjs-mjml/dist/index";
import { nextTick } from "vue";
export default {
  name: "Events.vue",
  components: { MarkdownEditor },
  data() {
    return {
      store: useStore(),
      editor: null,
      selectedLanguage: "en",
      activeEvent: null,
      turndownService: null,
      text: "",
      events: [],
      apps: [],
      mjmlEditor: {}, // Store the MJML editor instances
      showMjmlCode: {}, // Store visibility for MJML code
      showVariables: {},
      testOrders: [],
      testActionId: null,
      testSending: false,
      testForm: {
        email: "",
        phone: "",
        order_id: null,
      },
    };
  },
  mounted() {
    this.loadData();
  },
  computed: {
    languages() {
      return this.store.state.languages;
    },
    words() {
      if (this.activeEvent === null) {
        return [];
      }
      return this.activeEvent.EventVariables.map((e) => "" + e.variable + "");
    },
  },
  watch: {
    selectedLanguage() {
      nextTick(() => {
        this.rebuildEditors();
      });
    },
    events: {
      handler() {
        nextTick(() => {
          if (!this.activeEvent) return;
          this.loadEditorsForEvent(this.activeEvent);
        });
      },
      deep: true,
    },
    activeEvent: {
      handler() {
        nextTick(() => {
          this.rebuildEditors();
        });
      },
    },
  },
  methods: {
    getAppFormat(appId) {
      const app = this.apps.find((a) => a.app_id === appId);
      return app ? app.format : "text";
    },

    openTest(action) {
      this.testActionId = this.testActionId === action.id ? null : action.id;
    },

    toggleVariables(actionId) {
      this.showVariables = {
        ...this.showVariables,
        [actionId]: !this.showVariables[actionId],
      };
    },

    sendTest(action) {
      this.testSending = true;
      http
        .fetch(`/events/${action.id}/test`, this.testForm, true, "POST")
        .then(() => {
          alert("Test email sent.");
        })
        .finally(() => {
          this.testSending = false;
        });
    },

    rebuildEditors() {
      this.showMjmlCode = {};
      this.$nextTick(() => {
        Object.values(this.mjmlEditor).forEach((ed) => {
          try {
            ed.destroy();
          } catch (e) {
            console.log(e);
          }
        });

        this.mjmlEditor = {};

        this.activeEvent.eventActions
          .filter((a) => this.getAppFormat(a.app_id) === "mjml")
          .forEach((a) => {
            this.initMJMLEditor(a);
          });
      });
    },

    loadEditorsForEvent(event) {
      const actions = event.eventActions.filter(
        (e) => e.language_code === this.selectedLanguage
      );

      actions.forEach((action) => {
        if (this.getAppFormat(action.app_id) === "mjml") {
          this.initMJMLEditor(action);
        }
      });
    },
    // Initialize MJML editor for a specific action
    initMJMLEditor(action) {
      if (this.mjmlEditor[action.id]) return;

      // Initialize editor only when needed
      const containerId = "mjml-editor-" + action.id;
      const container = document.getElementById(containerId);

      if (container) {
        const editor = grapesjs.init({
          fromElement: false,
          container: "#" + containerId,
          plugins: [grapesJSMJML, "clean-default-buttons"],
          pluginsOpts: {
            [grapesJSMJML]: {
              /* ...options */
            },
          },
        });

        // Configure MJML editor options
        grapesjs.plugins.add("clean-default-buttons-" + action.id, (editor) => {
          // Wait until panels are ready
          editor.on("load", () => {
            const optionsPanel = editor.Panels.getPanel("options");
            const viewsPanel = editor.Panels.getPanel("views");
            const commandsPanel = editor.Panels.getPanel("commands");
            if (!optionsPanel) return; // safety check
            const keep = ["undo", "redo", "open-blocks", "open-sm"];

            optionsPanel.get("buttons").each((btn) => {
              if (btn === undefined) {
                return;
              }
              if (!keep.includes(btn.get("id"))) {
                btn.collection.remove(btn);
              }
            });
            viewsPanel.get("buttons").each((btn) => {
              if (btn === undefined) {
                return;
              }
              if (!keep.includes(btn.get("id"))) {
                btn.collection.remove(btn);
              }
            });
            commandsPanel.get("buttons").each((btn) => {
              if (btn === undefined) {
                return;
              }
              if (!keep.includes(btn.get("id"))) {
                btn.collection.remove(btn);
              }
            });
          });
        });

        // Add product block
        editor.BlockManager.add("product", {
          label: "Product",
          category: "E-commerce",
          content: `
            <mj-wrapper data-type="product">
              <mj-section>
                <mj-column width="20%">
                  <mj-image src="{{product.image}}" height="120px"></mj-image>
                </mj-column>
                <mj-column>
                  <mj-text font-size="15px" font-weight="700">
                    {{product.name}}
                  </mj-text>
                  <mj-text>
                    {{product.price}}
                  </mj-text>
                </mj-column>
              </mj-section>
            </mj-wrapper>
          `,
        });

        console.log(action);

        // Set initial content
        editor.on("load", () => {
          let content;

          if (action.text && action.text.trim() !== "") {
            content = action.text;
          } else {
            content = `
      <mjml>
        <mj-body>
          <mj-section>
            <mj-column>
              <mj-text>Hello</mj-text>
            </mj-column>
          </mj-section>
        </mj-body>
      </mjml>
    `;
          }

          try {
            editor.setComponents(content);
          } catch (e) {
            console.error("Failed to set MJML content", e);
          }
        });

        // Store the editor reference
        this.mjmlEditor[action.id] = editor;
      }
    },

    toggleMjmlCode(action) {
      if (!this.showMjmlCode[action.id]) {
        // Switching from editor view to code view
        const editor = this.mjmlEditor[action.id];
        if (editor) {
          action.text = editor.getHtml();
        }
      } else {
        // Switching from code view back to editor view
        const editor = this.mjmlEditor[action.id];
        if (editor && action.text) {
          try {
            editor.setComponents(action.text);
          } catch (e) {
            console.error("Failed to update MJML content on switch back", e);
          }
        }
      }

      this.showMjmlCode = {
        ...this.showMjmlCode,
        [action.id]: !this.showMjmlCode[action.id],
      };
    },

    getMjmlCode(action) {
      if (this.showMjmlCode[action.id]) {
        return action.text;
      }
      const editor = this.mjmlEditor[action.id];
      if (editor) {
        return editor.getHtml();
      }
      return "";
    },

    updateMjmlFromCode(action, code) {
      action.text = code;
    },

    convert(action) {
      let data = {
        text: action.text,
        title: action.title,
        delay: action.delay,
        app_id: action.app_id,
        language_code: this.selectedLanguage,
      };

      if (this.getAppFormat(action.app_id) === "mjml") {
        const editor = this.mjmlEditor[action.id];

        if (editor) {
          try {
            data.text = editor.getHtml();
          } catch (e) {
            console.error("MJML extraction failed", e);
          }
        }
      } else {
        data.text = cleanTables(action.text);
      }

      if (action.id !== undefined) {
        http.fetch("/events/" + action.id, data, true, "PUT");
      } else {
        data.event_id = this.activeEvent.id;

        http.fetch("/events", data, true).then((resp) => {
          action.id = resp.id;
        });
      }
    },
    loadData() {
      http.fetch("/events").then((data) => {
        this.events = data.events;
        this.apps = data.apps;
        this.activeEvent = this.events[0];
      });
      http.fetch("/events/test-data").then((data) => {
        this.testForm.email = data.user?.email || "";
        this.testForm.phone = data.user?.phone || "";
        this.testOrders = (data.orders || []).map((order) => ({
          ...order,
          label: `${order.pretty_id || order.id} (${order.created_at})`,
        }));
        this.testForm.order_id = this.testOrders[0]?.id || null;
      });
    },
    addNew() {
      this.activeEvent.eventActions.push({
        app_id: this.apps[0].app_id,
        delay: 0,
        event_id: this.activeEvent.id,
        template: null,
        text: "",
        title: "",
        language_code: this.selectedLanguage,
      });
    },
  },
};
</script>
<style>
.event,
.action {
  background: #f7f8ff;
  display: inline-block;
  padding: 1em 2em;
  border-radius: 10px;
  box-shadow: 0 0 1px 1px #ebecf3;
}

.action {
  background: #f1f1f1;
  box-shadow: 0 0 1px 1px #e4e1e1;
}

.test-email-form {
  padding: 1rem;
  border: 1px solid #dce2f4;
  border-radius: 8px;
  background: #fff;
  min-width: min(620px, 70vw);
}

.action-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.action-toolbar-left {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.variables-section {
  padding-top: 0.25rem;
}

@media (max-width: 768px) {
  .action-toolbar {
    flex-direction: column;
  }

  .test-email-form {
    min-width: 0;
  }
}

.event-line {
  border-left: 1px solid #ebecf3;
  height: 5em;
  width: 1px;
  margin: 0.4em;
  position: relative;
}
.event-line span {
  position: absolute;
  width: 100px;
  text-align: center;
  margin-left: -50px;
  background: #fff;
  margin-top: 1em;
}
</style>
